import type { ComponentChildren } from 'preact'

export interface BarListItem {
  label: string
  value: number
  /** Optional secondary figure shown muted after the value (e.g. conversions). */
  secondary?: string
  /** Tailwind gradient classes for the bar fill; defaults to the brand green. */
  barClass?: string
}

interface Props {
  title: string
  icon?: ComponentChildren
  items: BarListItem[]
  emptyMessage?: string
  /** Cap the rows rendered; the rest are dropped (already ranked server-side). */
  maxItems?: number
  /** Omit the `.tile` card chrome (border/padding) — for nesting inside another card. */
  bare?: boolean
  /** Tighter typography / spacing / bars — for contexts where the tile must stay short
   *  (e.g. the scroll-depth funnel sharing a row with single-number stat tiles). */
  dense?: boolean
  className?: string
  /** Show a shimmer skeleton instead of rows while the analytics fetch is in flight. */
  loading?: boolean
  /** Render an error message in place of rows when the fetch failed. */
  error?: string | null
}

/**
 * Compact ranked list with proportion bars, shared by the analytics surfaces (ORB-373):
 * top referrers, top UTM campaigns, channel breakdown, and a Contact's top landing pages.
 * Bars are scaled to the largest value in the (capped) set. Pass `bare` to drop the tile
 * chrome when embedding inside another card (e.g. the range-less Landing Page card); pass
 * `dense` to tighten the rows where the tile must stay short (e.g. the scroll-depth funnel).
 */
export function MetricBarList({ title, icon, items, emptyMessage = 'No data yet.', maxItems = 5, bare = false, dense = false, className = '', loading = false, error = null }: Props) {
  const rows = items.slice(0, maxItems)
  const max = rows.reduce((acc, r) => Math.max(acc, r.value), 0)

  // Dense tightens the gap, text, and bar so the tile reads shorter without losing the rows.
  const rowGap = dense ? 'gap-1.5' : 'gap-2.5'
  const labelText = dense ? 'text-xs' : 'text-sm'
  const barWrap = dense ? 'mt-0.5 h-1' : 'mt-1 h-1.5'
  const headerMb = dense ? 'mb-2' : 'mb-3'

  let body: ComponentChildren
  if (loading) {
    // Skeleton rows (label + bar) mirroring the data layout, matching the stat tiles' shimmer.
    body = (
      <ul className={`flex flex-col ${rowGap}`}>
        {Array.from({ length: maxItems }).map((_, i) => (
          <li key={i}>
            <div className="flex items-baseline justify-between gap-3">
              <div className="h-3 w-24 rounded-md bg-shimmer-light animate-shimmer" />
              <div className="h-3 w-10 rounded-md bg-shimmer-light animate-shimmer" />
            </div>
            <div className={`${barWrap} rounded-full bg-shimmer-light animate-shimmer`} />
          </li>
        ))}
      </ul>
    )
  } else if (error) {
    body = <div className="flex-1 flex items-center text-sm text-red-600 dark:text-red-400">{error}</div>
  } else if (rows.length === 0) {
    body = <div className="flex-1 flex items-center text-sm text-slate-500 dark:text-slate-400">{emptyMessage}</div>
  } else {
    body = (
      <ul className={`flex flex-col ${rowGap}`}>
        {rows.map(row => (
          <li key={row.label}>
            <div className="flex items-baseline justify-between gap-3">
              <span className={`${labelText} text-slate-700 dark:text-slate-200 truncate`} title={row.label}>{row.label}</span>
              <span className={`${labelText} font-semibold text-slate-900 dark:text-white tabular-nums whitespace-nowrap`}>
                {row.value.toLocaleString()}
                {row.secondary && <span className="ml-1 text-2xs font-normal text-slate-400 dark:text-slate-500">{row.secondary}</span>}
              </span>
            </div>
            <div className={`${barWrap} rounded-full bg-slate-100 dark:bg-slate-700/60 overflow-hidden`}>
              <div
                className={`h-full rounded-full ${row.barClass ?? 'bg-gradient-to-r from-primary-400 to-primary-500'}`}
                style={{ width: `${max > 0 ? Math.max(4, (row.value / max) * 100) : 0}%` }}
              />
            </div>
          </li>
        ))}
      </ul>
    )
  }

  return (
    <div className={(bare ? className : `tile ${className}`).trim()}>
      <div className={`flex items-center justify-between ${headerMb}`}>
        <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">{title}</h3>
        {icon}
      </div>
      {body}
    </div>
  )
}
